<template>
  <div class="Header" :class="[`Header--${color}`]">
    <h1 class="Header__heading">
      <router-link class="Header__logo" to="" @click="onLogoClick">
        <img
          src="@/assets/images/img_logo-spectee.png"
          alt="Spectee"
          width="112"
          height="48"
        />
      </router-link>
      <span class="Header__headingText">{{ heading }}</span>
    </h1>
    <ul class="Header__inner">
      <li v-if="showServiceMenu" class="Header__innerItem">
        <button
          ref="serviceRef"
          class="Header__menu"
          :class="{ [`Header__menu--active`]: isServiceOpen }"
          :aria-label="$t('header.serviceMenu')"
          @click="openServiceMenu"
        >
          <Icon class="Header__icon" icon-name="App" />
        </button>
      </li>
      <li class="Header__innerItem">
        <button
          ref="accountRef"
          class="Header__account"
          :class="{ [`Header__account--active`]: isAccountOpen }"
          :aria-label="$t('header.accountMenu')"
          @click="openAccountMenu"
        >
          <span class="Header__accountText">{{ userName }}</span>
          <Icon class="Header__arrowIcon" icon-name="Down" />
        </button>
      </li>
    </ul>
    <!-- ServiceMenu -->
    <div v-if="showServiceMenu && isServiceOpen" class="Header__menuBox">
      <ul>
        <li v-for="item in menuList" :key="item.text" class="Header__menuItem">
          <a class="Header__menuLink" :href="item.link">
            <Icon class="Header__menuIcon" :icon-name="item.iconName" />
            {{ item.text }}
          </a>
        </li>
      </ul>
    </div>
    <!-- AccountMenu -->
    <div v-if="isAccountOpen" class="Header__accountBox">
      <ul>
        <li class="Header__accountItem">
          <span class="Header__company">{{ companyName }}</span>
        </li>
        <li class="Header__accountItem">
          <span class="Header__user">{{ userName }}</span>
        </li>
        <!--TODO: URLを設定-->
        <li class="Header__accountItem">
          <div class="Header__logout">
            <button class="Header__logoutButton" @click="logout()">
              {{ $t('header.logout') }}
            </button>
          </div>
          <!-- <div class="Header__logout">
            <Button
              tag="button"
              label="ログアウト"
              @click="logout"
              dynamics="tertiary"
            />
          </div> -->
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { useRoute, useRouter } from 'vue-router';
import Icon from '@/components/atoms/IconBase.vue';
import getAccount from '@/common/getAccount';

defineOptions({
  name: 'HeaderBase',
});

interface Props {
  color?: 'blue' | 'black';
  showServiceMenu?: boolean;
}

withDefaults(defineProps<Props>(), {
  color: 'blue',
  showServiceMenu: true,
});

// methods.logout と名前が衝突すると setup の戻り値が優先され、
// methods.logout が呼ばれなくなるためリネームする
const { logout: auth0Logout } = useAuth0();
const route = useRoute();
const router = useRouter();

const accountRef = ref<HTMLButtonElement | null>(null);

const isServiceOpen = ref(false);
const isAccountOpen = ref(false);
const heading = ref('');
const userName = ref('');
const companyName = ref('');
const menuList = ref<{ text: string; link: string; iconName: string }[]>([
  // { text: 'アカウント', link: '/account', iconName: 'User' },
  // {
  //   text: '管理システム',
  //   link: '/',
  //   iconName: 'SpecteeLogoBlack',
  // },
  // { text: 'Spectee Pro', link: '#3', iconName: 'SpecteeLogoBlue' },
  // { text: '通信障害', link: '#3', iconName: 'SpecteeLogoBlue' },
  // { text: 'Spectee for scm', link: '#3', iconName: 'SpecteeLogoRed' },
  // { text: '拠点災害コンサル', link: '#3', iconName: 'SpecteeLogoRed' },
]);

// ログイン情報からアカウント名とユーザー名を設定する
companyName.value = getAccount.getAccountDisplayName();
userName.value = getAccount.getUserNickname();

function openAccountMenu(): void {
  // サービスメニューが開いていたら閉じる
  if (isServiceOpen.value) {
    isServiceOpen.value = !isServiceOpen.value;
  }
  isAccountOpen.value = !isAccountOpen.value;
}

function openServiceMenu(): void {
  // アカウントメニューが開いていたら閉じる
  if (isAccountOpen.value) {
    isAccountOpen.value = !isAccountOpen.value;
  }
  isServiceOpen.value = !isServiceOpen.value;
}

function closeAccountWindow(event: MouseEvent): void {
  const accountButton = accountRef.value;
  const accountBox = document.querySelector('.Header__accountBox');

  if (
    isAccountOpen.value &&
    accountButton &&
    accountBox &&
    !accountButton.contains(event.target as Node | null) &&
    !accountBox.contains(event.target as Node | null)
  ) {
    isAccountOpen.value = false;
  }
}

function logout(): void {
  // returnTo を指定しないと Auth0 の Allowed Logout URLs の先頭に遷移してしまう
  auth0Logout({
    logoutParams: {
      returnTo: `${window.location.origin}/`,
    },
    async openUrl(url) {
      window.location.replace(url);
    },
  });
}

function onLogoClick(): void {
  if (route.path === '/') {
    window.location.reload();
  } else {
    router.push('/');
  }
}

onMounted(() => {
  document.addEventListener('click', closeAccountWindow);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeAccountWindow);
});
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_forward.scss' as *;

.Header {
  z-index: $service-z-index-ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: $service-unit * 12;
  padding-left: $service-unit * 4;

  &--blue {
    background-color: $color-blue-60;
  }

  &--black {
    background-color: $color-gray-90;
  }

  &__heading {
    display: flex;
    align-items: center;
    font-size: $service-font-size-p;
    font-weight: bold;
  }

  &__logo {
    margin-top: $service-unit * 0.5;
  }

  &__headingText {
    display: inline-block;
    margin-left: $service-unit * 2;
  }

  &__inner {
    display: flex;
    align-items: center;
  }

  &__menu {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $service-unit * 12;
    height: $service-unit * 12;
    cursor: pointer;
    background-color: inherit;
  }

  & &__icon {
    width: $service-unit * 6;
    height: $service-unit * 6;
  }

  &__account {
    display: flex;
    align-items: center;
    height: $service-unit * 12;
    padding: 0 $service-unit * 4;
    cursor: pointer;
    background-color: inherit;
  }

  &__accountText {
    @include ellipsis;

    display: inline-block;
    max-width: $service-unit * 30;
    font-size: $service-font-size-pp;
  }

  & &__arrowIcon {
    width: $service-unit * 4;
    height: $service-unit * 4;
    transition: 0.2s ease-out;
  }

  &__account--active &__arrowIcon {
    transform: rotate(180deg);
  }

  &--blue &__heading,
  &--black &__heading,
  &--blue &__account,
  &--black &__account,
  &--blue &__icon,
  &--black &__icon {
    color: $color-white-100;
  }

  &--black &__menu:hover,
  &--black &__account:hover,
  &--black &__menu--active,
  &--black &__account--active {
    background-color: rgb(255 255 255 / 20%);
  }

  &--blue &__menu:hover,
  &--blue &__account:hover,
  &--blue &__menu--active,
  &--blue &__account--active {
    background-color: rgb(000 000 000 / 20%);
  }

  &__menuBox {
    position: absolute;
    top: $service-unit * 14;
    right: $service-unit * 2;
    z-index: 1000;
    width: $service-unit * 90;
    background-color: $color-white-100;
    box-shadow: $service-shadow-mf;
  }

  &__menuItem {
    &:not(:first-child) {
      border-top: 1px solid $color-gray-30;
    }
  }

  &__menuLink {
    display: flex;
    align-items: center;
    padding: $service-unit * 4;
    font-size: $service-font-size-p;
    font-weight: bold;
    color: $color-gray-80;

    &:hover {
      text-decoration: none;
      background-color: $color-gray-20;
    }
  }

  & &__menuIcon {
    flex-shrink: 0;
    width: $service-unit * 7;
    height: $service-unit * 7;
    margin-right: $service-unit * 3;
  }

  &__accountBox {
    position: absolute;
    top: $service-unit * 12;
    right: 0;
    z-index: 1000;
    width: $service-unit * 60;
    padding: $service-unit * 4 0 $service-unit * 2;
    background-color: $color-white-100;
    box-shadow: $service-shadow-p;
  }

  &__accountItem {
    padding: $service-unit * 3 0;

    &:first-child {
      padding-top: 4px;
      padding-bottom: 0;
      border-top: 0;
    }

    &:last-child {
      padding: 0;
      border-top: 0.5px solid #c0c2c5;
    }
  }

  &__company,
  &__user {
    display: block;
    word-break: break-all;
    overflow-wrap: break-word;
    padding-left: $service-unit * 4;
  }

  &__company {
    font-size: $service-font-size-pp;
    color: $color-gray-70;
  }

  &__user {
    font-size: $service-font-size-p;
    font-weight: bold;
  }

  &__button {
    margin-top: $service-unit * 2;
    text-align: center;
  }

  &__logout {
    text-align: center;
  }

  &__logoutButton {
    width: 100%;
    padding: $service-unit * 3 0;
    font-size: $service-font-size-pp;
    color: $color-blue-60;
    cursor: pointer;
    background-color: $color-white-100;
    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    &__innerItem:nth-child(2) {
      width: 100%;
    }

    &__accountText {
      display: none;
    }

    &__accountBox {
      width: 100%;
    }

    &__menuBox {
      width: 90%;
    }
  }
}
</style>
